export type UIMode = "classic" | "node";
export type Provider = "oauth" | "api";
export type Quality = "low" | "medium" | "high";
export type Format = "png" | "jpeg" | "webp";
export type Moderation = "low" | "auto";
export type Background = "auto" | "opaque" | "transparent";
export type ReferenceRole = "identity" | "product" | "composition" | "style" | "background";
export type Count = 1 | 2 | 4;

export type SizePreset =
  | "1024x1024"
  | "1536x1024"
  | "1024x1536"
  | "1360x1024"
  | "1024x1360"
  | "1824x1024"
  | "1024x1824"
  | "2048x2048"
  | "2048x1152"
  | "1152x2048"
  | "3824x2160"
  | "2160x3824"
  | "auto"
  | "custom";

// Lineage of a single reference image used to produce a generated result.
//   kind: "history"  — the ref was an existing /generated/<filename> image
//                      (e.g. "현재 결과 사용"), so clicking the thumbnail
//                      can navigate the lightbox to that history item.
//   kind: "uploaded" — the ref was a fresh upload, persisted on the server
//                      under /generated/.refs/<hash>.png. Clicking just
//                      previews the image; no navigation target exists.
export type ReferenceImageRef = {
  kind: "history" | "uploaded";
  hash: string;
  filename?: string;
  sourceUrl: string;
};

export type GenerateItem = {
  image: string;
  url?: string;
  filename?: string;
  prompt?: string;
  originalPrompt?: string;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptRuntime?: PromptRuntime | null;
  elapsed?: number;
  provider?: string;
  imageRoute?: string | null;
  imageModel?: string | null;
  responsesModel?: string | null;
  codexAccount?: string | null;
  quality?: string;
  size?: string;
  width?: number | null;
  height?: number | null;
  resolution?: string | null;
  moderation?: string;
  format?: Format | string;
  background?: Background | string;
  compression?: number;
  responseId?: string | null;
  imageCallId?: string | null;
  previousResponseId?: string | null;
  usage?: { total_tokens?: number } & Record<string, unknown>;
  thumb?: string;
  web?: string;
  createdAt?: number;
  favorite?: boolean;
  sessionId?: string | null;
  // 노드 모드에서 생성된 이미지면 server sidecar에 저장된 노드 식별자가
  // 함께 따라온다 (server.js loadHistoryRows 매핑). 갤러리에서 클릭/삭제
  // 시 해당 노드로 점프하거나 함께 지우는 데 사용.
  nodeId?: string | null;
  clientNodeId?: string | null;
  kind?: string | null;
  references?: ReferenceImageRef[];
};

export type PromptRuntime = {
  prompt?: string | null;
  userPrompt?: string | null;
  developerPrompt?: string | null;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptMutationDisabled?: boolean;
  userPromptMutated?: boolean;
  hasRefs?: boolean;
  toolNames?: string[];
  route?: string | null;
  model?: string | null;
  imageModel?: string | null;
  reasoningEffort?: string | null;
  // What the backend ACTUALLY applied, which is not what we asked for. The
  // Codex OAuth path rewrites the image_generation tool arguments on every
  // call (model/quality/size/background), so the request values above are a
  // record of intent only. Present from 2026-09-09 onward; older history
  // rows leave it undefined.
  effectiveImage?: {
    model?: string | null;
    quality?: string | null;
    size?: string | null;
    background?: string | null;
    outputFormat?: string | null;
  } | null;
};

// Hint sent to the server alongside `references` so it can label each
// uploaded reference as either "from a history file" or "fresh upload".
// The server still re-hashes every byte to verify, so a wrong/missing
// hint is harmless — it just falls back to the uploaded path.
export type ReferenceMetaHint = {
  kind: "history" | "uploaded";
  filename?: string;
};

export type GenerateSingleResponse = {
  image: string;
  elapsed: number;
  filename: string;
  usage?: GenerateItem["usage"];
  provider: string;
  imageRoute?: string | null;
  imageModel?: string | null;
  responsesModel?: string | null;
  quality?: string;
  size?: string;
  width?: number | null;
  height?: number | null;
  resolution?: string | null;
  moderation?: string;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptRuntime?: PromptRuntime | null;
  responseId?: string | null;
  imageCallId?: string | null;
  background?: Background | string;
  backgroundRequested?: Background | string;
  backgroundFallback?: boolean;
  alphaVerified?: boolean;
  responseChainFallback?: boolean;
  compression?: number;
};

export type GenerateMultiResponse = {
  images: Array<{ image: string; filename: string; references?: ReferenceImageRef[] }>;
  elapsed: number;
  count: number;
  usage?: GenerateItem["usage"];
  provider: string;
  imageRoute?: string | null;
  imageModel?: string | null;
  responsesModel?: string | null;
  quality?: string;
  size?: string;
  width?: number | null;
  height?: number | null;
  resolution?: string | null;
  moderation?: string;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptRuntime?: PromptRuntime | null;
  responseId?: string | null;
  imageCallId?: string | null;
  background?: Background | string;
  backgroundRequested?: Background | string;
  backgroundFallback?: boolean;
  alphaVerified?: boolean;
  responseChainFallback?: boolean;
  compression?: number;
};

export type GenerateResponse = GenerateSingleResponse | GenerateMultiResponse;

export function isMultiResponse(r: GenerateResponse): r is GenerateMultiResponse {
  return Array.isArray((r as GenerateMultiResponse).images);
}

export type GenerateRequest = {
  prompt: string;
  quality: Quality;
  size: string;
  format: Format;
  moderation: Moderation;
  background?: Background;
  compression?: number;
  partialImages?: 0 | 1 | 2 | 3;
  action?: "auto" | "generate" | "edit";
  previousResponseId?: string;
  referenceRoles?: ReferenceRole[];
  provider: Provider;
  n: number;
  image?: string;
  mask?: string;
  references?: string[];
  // Lineage hint per reference. Index-aligned with `references`. Server
  // re-hashes to verify, so a wrong hint is harmless.
  referenceMeta?: ReferenceMetaHint[];
  requestId?: string;
  maxAttempts?: number;
  // Pre-enhance original (only set when EnhanceModal applied a rewrite).
  // Server stores it in the sidecar so history can show what the user typed
  // before the model expanded it.
  originalPrompt?: string;
  // Sexy-tune metadata: which outfit-pool module produced this prompt. The
  // server records it in the sidecar so we can compute per-module pass rates
  // and feed those back into weighted sampling.
  outfitModule?: OutfitModuleMeta;
  // Batch tracking: when a client-side fanout (e.g. 텍스트 일괄 31 prompts)
  // mints these, every /api/generate call lands in
  // generated/.batches/<batchId>/<batchIndex>.json so the whole burst can be
  // inspected as one entity. batchSource is a free-form tag like "txt-batch"
  // / "outfit-pool" so future batch-driving UIs can be distinguished.
  batchId?: string;
  batchIndex?: number;
  batchTotal?: number;
  batchSource?: string;
  // 좌측 패널 "기본 프롬프트(시스템)" 섹션. 사용자가 편집한 텍스트.
  // 비어있거나 includeSystemPrompt=false 면 시스템 프롬프트 미주입.
  systemPrompt?: string;
  includeSystemPrompt?: boolean;
};

export type OutfitModuleMeta = {
  id: string;
  label: string;
  category: string;
  risk: "low" | "medium" | "high";
};

export type AttemptLog = {
  attempt: number;
  promptUsed: string;
  compliantVariant: boolean;
  ok: boolean;
  errorMessage: string | null;
  errorCode: string | null;
  durationMs: number;
  startedAt: number;
  promptRuntime?: PromptRuntime | null;
  // "왜 실패했나"의 근거. 서버 attempt 로그가 이미 기록하던 값들로,
  // 실패 상세 팝업과 생성 로그가 그대로 보여준다.
  refusalText?: string | null;
  outputText?: string | null;
  reasoningSummary?: string | null;
  violationCategories?: string[] | null;
  eventTypeCounts?: Record<string, number> | null;
};

export type GenerationLogItem = {
  id: string;
  status: "success" | "failed";
  createdAt: number;
  endpoint: "generate" | "edit" | "node";
  prompt: string | null;
  originalPrompt?: string | null;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptRuntime?: PromptRuntime | null;
  imageRoute?: string | null;
  imageModel?: string | null;
  responsesModel?: string | null;
  quality: string | null;
  size: string | null;
  format: string | null;
  moderation: string | null;
  maxAttempts: number | null;
  attempts: AttemptLog[];
  referenceCount: number;
  references?: ReferenceImageRef[];
  filename: string | null;
  url: string | null;
  sessionId: string | null;
  requestId?: string | null;
  errorCode: string | null;
  errorMessage: string | null;
};

export type OAuthStatus = {
  status: "ready" | "auth_required" | "offline" | "starting";
  models?: string[];
};

export type BillingResponse = {
  credits?: { total_granted?: number; total_used?: number };
  costs?: { data?: Array<{ results: Array<{ amount?: { value?: number } }> }> };
  oauth?: boolean;
  apiKeyValid?: boolean;
  apiKeySource?: "none" | "env" | "config";
};
